const LOGIN_APP_OVERRIDE_COOKIE = 'login_app_override'

/**
 * Get cookie value by name from document.cookie
 */
function getCookie(name: string): string | undefined {
  const match = document.cookie.split(';').find(c => c.trim().startsWith(`${name}=`))
  if (match) {
    return match.trim().split('=')[1]
  }
  return undefined
}

/**
 * Get the effective login_app_enabled value, applying cookie override if present.
 *
 * Precedence:
 * - Cookie 'enabled' -> true (QA override ON)
 * - Cookie 'disabled' -> false (QA override OFF)
 * - Cookie absent -> serverFlag value
 *
 * Note: No network logging here - this function is called from responseErrorInterceptor,
 * and network-backed logging would cause infinite recursion when logging endpoint fails.
 *
 * @param serverFlag - The loginAppEnabled value from publicAppSettings
 * @returns Effective login_app_enabled boolean
 */
export function isLoginAppEnabled(serverFlag: boolean): boolean {
  const overrideValue = getCookie(LOGIN_APP_OVERRIDE_COOKIE)

  if (overrideValue === 'enabled') {
    return true
  }

  if (overrideValue === 'disabled') {
    return false
  }

  return serverFlag
}

/**
 * Check if the login_app_override cookie is currently set.
 * Useful for logging/debugging.
 */
export function hasLoginAppOverride(): boolean {
  const value = getCookie(LOGIN_APP_OVERRIDE_COOKIE)
  return value === 'enabled' || value === 'disabled'
}

const LOGIN_APP_AUTHORIZE_PATH = '/admin/oauth2/authorize'
const ENTRY_PATH_MAX_LENGTH = 256

export interface LoginAppAuthorizeParams {
  accountHint?: string | null
  entryPath?: string | null
}

export function buildLoginAppAuthorizeUrl(params: LoginAppAuthorizeParams = {}): string {
  const searchParams = new URLSearchParams()
  searchParams.set('connection', 'login_app')
  if (params.accountHint) {
    searchParams.set('account_hint', params.accountHint)
  }
  if (params.entryPath) {
    searchParams.set('entry_path', params.entryPath)
  }
  return `${LOGIN_APP_AUTHORIZE_PATH}?${searchParams.toString()}`
}

export function getCurrentEntryPath(): string | undefined {
  const entryPath = `${window.location.pathname}${window.location.search}`
  if (!entryPath.startsWith('/') || entryPath.startsWith('//') || entryPath.includes('://')) {
    return undefined
  }
  if (entryPath.length > ENTRY_PATH_MAX_LENGTH) {
    return undefined
  }
  return entryPath
}

export function isOnOAuth2Page(): boolean {
  return window.location.pathname.includes('/oauth2/')
}
